import { X } from 'lucide-react';
import React from 'react';
import { IoStatsChart } from "react-icons/io5";

const StatsModal = ({ 
  showStatsModal, 
  selectedCategory, 
  closeStatsModal, 
  formatValue, 
  milestones, 
  currentTrophyIndices 
}) => {
  
  if (!showStatsModal || !selectedCategory) return null;

  // Récupérer la valeur actuelle du trophée
  const currentValue = milestones[selectedCategory] && currentTrophyIndices[selectedCategory] !== undefined
    ? milestones[selectedCategory][currentTrophyIndices[selectedCategory]] || 0 
    : 0;

  // Calcul du percentile selon la catégorie
  const getPercentile = (category, value) => {
    switch (category) {
      case 'patrimoine':
        if (value >= 50000000) return 99.9; // Top 0.1%
        if (value >= 10000000) return 99.5; // Top 0.5%
        if (value >= 5000000) return 99; // Top 1%
        if (value >= 2239200) return 98; // Top 2%
        if (value >= 1000000) return 95; // Top 5%
        if (value >= 716300) return 90; // Top 10%
        if (value >= 500000) return 80;
        if (value >= 400000) return 70;
        if (value >= 300000) return 60;
        if (value >= 177200) return 50; // Médiane
        if (value >= 100000) return 30;
        if (value >= 50000) return 20;
        if (value >= 25000) return 15;
        if (value >= 10000) return 10;
        if (value >= 1000) return 5;
        return 1; // Pour les très faibles valeurs
        
      case 'cashflow':
        if (value >= 50000) return 99.9;
        if (value >= 20000) return 99.5;
        if (value >= 10000) return 99;
        if (value >= 5000) return 98;
        if (value >= 2000) return 95;
        if (value >= 1600) return 90;
        if (value >= 1200) return 80;
        if (value >= 800) return 65;
        if (value >= 400) return 40;
        if (value >= 200) return 25;
        if (value >= 100) return 15;
        if (value >= 50) return 10;
        if (value >= 10) return 5;
        return 1;
        
      case 'reseau':
        if (value >= 10000) return 99.9;
        if (value >= 5000) return 99.5;
        if (value >= 2000) return 99;
        if (value >= 1000) return 98;
        if (value >= 500) return 95;
        if (value >= 300) return 90;
        if (value >= 194) return 75;
        if (value >= 150) return 60;
        if (value >= 100) return 40;
        if (value >= 50) return 25;
        if (value >= 25) return 15;
        if (value >= 10) return 10;
        if (value >= 5) return 5;
        return 1;
        
      case 'maitrise':
        if (value >= 100) return 99.9;
        if (value >= 50) return 99.5;
        if (value >= 20) return 99;
        if (value >= 10) return 98;
        if (value >= 5) return 95;
        if (value >= 4) return 90;
        if (value >= 3) return 80;
        if (value >= 2) return 60;
        if (value >= 1) return 30;
        if (value >= 0.5) return 15;
        if (value >= 0.1) return 5;
        return 1;
        
      default:
        return 50;
    }
  };

  // Données de référence
  const referenceData = {
    patrimoine: {
      title: 'Patrimoine immobilier',
      median: 177200,
      average: 280000,
      top10: '716K€',
      description: 'des ménages français'
    },
    cashflow: {
      title: 'Cashflow mensuel',
      median: 400,
      average: 650,
      top10: '1 600€',
      description: 'des investisseurs immobiliers français'
    },
    reseau: {
      title: 'Réseau professionnel',
      median: 110,
      average: 150,
      top10: '300',
      description: 'des professionnels français actifs'
    },
    maitrise: {
      title: 'Opérations immobilières',
      median: 1,
      average: 1.8,
      top10: '3',
      description: 'des investisseurs immobiliers français'
    }
  };

  const percentile = getPercentile(selectedCategory, currentValue);
  const topPercent = parseFloat((100 - percentile).toFixed(4));
  const categoryData = referenceData[selectedCategory];

  // Calcul de la position du trait basé sur le percentile
  const getTraitPosition = (percentile) => {
    // Largeur totale du graphique : 4 barres de 64px + 3 espaces de 32px = 352px
    const totalWidth = 352;
    // Position basée sur le percentile (0-100%)
    const position = (percentile / 100) * totalWidth;
    // Centrer sur la largeur totale (400px - 352px = 48px de marge / 2 = 24px)
    return position - (totalWidth / 2);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 max-w-lg w-full mx-4">
        
        {/* En-tête */}
        <div className="bg-gray-100 px-2 py-1 border-b border-gray-200 -mx-6 -mt-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <IoStatsChart className="w-5 h-5 text-gray-600" />
              </div>
              <div className="w-px h-9 bg-gray-300"></div>
              <div>
                <h3 className="text-sm font-medium text-gray-700">Positionnement National</h3>
                <p className="text-xs text-gray-500">Comparaison avec la population française</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Drapeau français */}
              <div className="flex border border-gray-300 rounded-sm">
                <div className="w-2 h-4 bg-blue-600"></div>
                <div className="w-2 h-4 bg-white"></div>
                <div className="w-2 h-4 bg-red-600"></div>
              </div>
              <button
                onClick={closeStatsModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Titre principal */}
        <div className="text-center mb-8">
          <div className="text-3xl font-bold text-[#18574F] mb-2">
            Top {topPercent}%
          </div>
          <p className="text-gray-600 text-xs">
            Vous vous situeriez dans le top {topPercent}% {categoryData.description}
          </p>
        </div>

        {/* Graphique */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-8">
          <div className="flex justify-center">
            <div className="relative" style={{ height: '120px', width: '400px' }}>
              
              {/* Barres de l'histogramme */}
              <div className="flex items-end justify-center space-x-8 mt-4" style={{ height: '80px' }}>
                <div className="relative flex flex-col items-center">
                  <div className="w-16 bg-red-200 border border-red-300 rounded-t" style={{ height: '60px' }}></div>
                  <div className="text-xs text-center mt-3 text-red-600 font-medium">Q1</div>
                </div>
                <div className="relative flex flex-col items-center">
                  <div className="w-16 bg-orange-200 border border-orange-300 rounded-t" style={{ height: '45px' }}></div>
                  <div className="text-xs text-center mt-3 text-orange-600 font-medium">Q2</div>
                </div>
                <div className="relative flex flex-col items-center">
                  <div className="w-16 bg-yellow-200 border border-yellow-300 rounded-t" style={{ height: '30px' }}></div>
                  <div className="text-xs text-center mt-3 text-yellow-600 font-medium">Q3</div>
                </div>
                <div className="relative flex flex-col items-center">
                  <div className="w-16 bg-green-200 border border-green-300 rounded-t" style={{ height: '20px' }}></div>
                  <div className="text-xs text-center mt-3 text-green-600 font-medium">Q4</div>
                </div>
              </div>

              {/* Position de l'utilisateur - CORRIGÉ */}
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" style={{ height: '80px' }}>
                <div
                  className="relative transition-all duration-500 h-full"
                  style={{
                    left: `${getTraitPosition(percentile)}px`
                  }}
                >
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-bold text-[#18574F] text-center whitespace-nowrap">
                    {formatValue(selectedCategory, currentValue)}
                  </div>
                  <div className="w-0.5 h-full border-l-2 border-dashed border-[#18574F]"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="text-center p-4 bg-white rounded border border-gray-200">
            <div className="text-xl font-semibold text-gray-900">
              {formatValue(selectedCategory, categoryData.median)}
            </div>
            <div className="text-sm text-gray-500">Médiane</div>
          </div>
          <div className="text-center p-4 bg-white rounded border border-gray-200">
            <div className="text-xl font-semibold text-gray-900">
              {formatValue(selectedCategory, categoryData.average)}
            </div>
            <div className="text-sm text-gray-500">Moyenne</div>
          </div>
          <div className="text-center p-4 bg-[#c8d7d6] rounded border">
            <div className="text-xl font-semibold">
              {categoryData.top10}
            </div>
            <div className="text-sm text-gray-500">Top 10%</div>
          </div>
        </div>

        {/* Bouton fermer */}
        <div className="text-center">
          <button
            onClick={closeStatsModal}
            className="bg-gray-800 text-white px-6 py-2 rounded border border-gray-300 hover:bg-gray-700 transition-colors"
          >
            Fermer
          </button>
        </div>

        {/* Sources */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {selectedCategory === 'patrimoine' && 'Source : INSEE (enquête Histoire de vie et Patrimoine 2021-2024)'}
            {selectedCategory === 'cashflow' && 'Source : Études sectorielles investissement locatif français'}
            {selectedCategory === 'reseau' && 'Source : LinkedIn France (statistiques utilisateurs 2024)'}
            {selectedCategory === 'maitrise' && 'Source : INSEE (transactions immobilières françaises)'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StatsModal;